import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import {
  type CallAdapter,
  type CallAdapterMode,
  createCallAdapter,
  type GuestMeetingPreview,
  guestMeetingQueryOptions,
  joinErrorInfo,
  type TrackSnapshot,
} from '@/entities/call'
import { GuestJoin } from '@/features/guest-join'
import { toBackgroundOption, useBackgroundSelection } from '@/features/set-background'
import {
  CameraPreview,
  DevicePicker,
  useDeviceSelection,
  useMediaDevices,
} from '@/features/setup-devices'
import { NoiseSuppressionToggle } from '@/features/suppress-noise'
import { runtimeConfig } from '@/shared/config'
import { formatMeetingStart } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { PageHeader } from '@/shared/ui/page-header'
import { Spinner } from '@/shared/ui/spinner'
import { StatusScreen } from '@/shared/ui/status-screen'
import { ZoneNote } from '@/shared/ui/zone-note'

export const Route = createFileRoute('/guest/$token')({
  head: () => ({ meta: [{ title: 'Встреча · STIT Телемост' }] }),
  component: GuestPage,
})

const mode: CallAdapterMode = runtimeConfig.API_MODE === 'mock' ? 'fake' : 'real'

// Гейт до prejoin: битая ссылка / завершённая / отменённая встреча получают
// полноэкранный статус вместо формы имени и тоста на неизбежный 409.
function GuestPage() {
  const { token } = Route.useParams()
  const preview = useQuery(guestMeetingQueryOptions(token))

  if (preview.isPending) {
    return (
      <section className="container-x flex justify-center px-6 py-24">
        <Spinner label="Открываем встречу" />
      </section>
    )
  }
  if (preview.isError) {
    const info = joinErrorInfo(preview.error)
    if (info.kind === 'not-found') {
      return (
        <StatusScreen
          title="Ссылка недействительна"
          message="Встреча по этой ссылке не найдена. Проверьте, что ссылка скопирована целиком, или попросите у организатора новую."
        />
      )
    }
    return (
      <StatusScreen
        title="Не удалось открыть встречу"
        message="Проверьте соединение и обновите страницу."
      >
        <Button onClick={() => void preview.refetch()}>Повторить</Button>
      </StatusScreen>
    )
  }
  if (preview.data.state === 'ended') {
    return (
      <StatusScreen
        title="Встреча уже завершена"
        message={`«${preview.data.title}» закончилась. Если встреча продолжается по новой ссылке, попросите её у организатора.`}
      />
    )
  }
  if (preview.data.state === 'cancelled') {
    return (
      <StatusScreen
        title="Встреча отменена"
        message={`Организатор отменил «${preview.data.title}».`}
      />
    )
  }
  return <GuestPrejoin token={token} preview={preview.data} />
}

function GuestPrejoin({ token, preview }: { token: string; preview: GuestMeetingPreview }) {
  const { cameras, microphones, speakers, error } = useMediaDevices()
  const camEnabled = useDeviceSelection((s) => s.camEnabled)
  const camId = useDeviceSelection((s) => s.camId)
  // До входа в lobby гостевой cookie ещё нет — галерея /api/backgrounds
  // недоступна, доступен только blur (в комнате гостю откроются системные фоны).
  const selection = useBackgroundSelection((s) => s.selection)
  const setNone = useBackgroundSelection((s) => s.setNone)
  const setBlur = useBackgroundSelection((s) => s.setBlur)

  const [previewTrack, setPreviewTrack] = useState<TrackSnapshot | null>(null)
  const previewAdapterRef = useRef<CallAdapter | null>(null)
  const genRef = useRef(0)

  // Превью на отдельном адаптере — только startLocalPreview, без connect;
  // на unmount поток освобождается, чтобы комната захватила камеру.
  // camId в зависимостях: выбор камеры обязан менять картинку, а не только стор.
  useEffect(() => {
    if (!camEnabled) return
    genRef.current += 1
    const gen = genRef.current
    let stop: (() => void) | null = null

    void createCallAdapter(mode)
      .then(async (a) => {
        const preview = await a.startLocalPreview({
          video: true,
          audio: false,
          ...(camId ? { deviceId: camId } : {}),
        })
        if (genRef.current !== gen) {
          preview.stop()
          void a.disconnect()
          return
        }
        previewAdapterRef.current = a
        // Применяем выбранный фон к превью, чтобы гость проверил его до входа.
        await a.setBackground(toBackgroundOption(useBackgroundSelection.getState().selection))
        stop = () => {
          preview.stop()
          void a.disconnect()
          previewAdapterRef.current = null
        }
        setPreviewTrack(preview.track)
      })
      // Камера занята другим приложением или доступ отозван: превью просто не
      // появится — вход в звонок это не блокирует.
      .catch(() => {})

    return () => {
      genRef.current += 1
      stop?.()
    }
  }, [camEnabled, camId])

  // Живое переключение фона в превью (гостю доступны none/blur).
  useEffect(() => {
    void previewAdapterRef.current?.setBackground(toBackgroundOption(selection))
  }, [selection])

  return (
    <section className="container-x px-6 py-10">
      <PageHeader
        title={preview.title}
        eyebrow="Гость"
        // Гость пришёл по внешней ссылке: шапки продукта с бейджем зоны у него
        // может не быть перед глазами (на узком экране она скрыта), а время
        // встречи ему называют. Поэтому зона стоит прямо за временем и со
        // смещением — гость нередко в другой стране.
        description={
          preview.state === 'scheduled' ? (
            <>
              Встреча ещё не началась
              {preview.scheduledAt && (
                <>
                  {` (запланирована на ${formatMeetingStart(preview.scheduledAt)} `}
                  <ZoneNote full />
                  {')'}
                </>
              )}
              . Представьтесь и подождите в лобби: организатор скоро подключится
            </>
          ) : (
            'Проверьте камеру и представьтесь. Организатор впустит вас во встречу'
          )
        }
      />

      <div className="grid gap-6 md:grid-cols-2 md:items-start">
        {/* Десктоп: имя и вход — сразу под вебкой (главное действие гостя).
            Мобила: колонка распадается (contents), форма входа уходит в конец
            страницы — кнопка входа внизу, а не зажата между вебкой и настройками. */}
        <div className="max-md:contents md:flex md:flex-col md:gap-4">
          <CameraPreview track={camEnabled ? previewTrack : null} name="Гость" />
          <div className="max-md:order-last">
            <GuestJoin token={token} />
          </div>
        </div>

        <div className="flex flex-col gap-6">
          {error && <p className="text-accent">{error}</p>}
          <DevicePicker
            cameras={cameras}
            microphones={microphones}
            speakers={speakers}
            withChecks
          />

          <div className="flex flex-col gap-2">
            <span className="label">Фон камеры</span>
            <div className="flex gap-2">
              <Button
                variant={selection.kind === 'none' ? 'primary' : 'secondary'}
                size="sm"
                onClick={setNone}
              >
                Без фона
              </Button>
              <Button
                variant={selection.kind === 'blur' ? 'primary' : 'secondary'}
                size="sm"
                onClick={setBlur}
              >
                Размыть
              </Button>
            </div>
          </div>

          <NoiseSuppressionToggle />
        </div>
      </div>
    </section>
  )
}
